import React from 'react';
import { useTranslation } from 'react-i18next';

import PolicyNavigation from './PolicyNavigation';

interface GDPRComplianceContentProps {
  onNavigate?: (view: string) => void;
}

const GDPRComplianceContent: React.FC<GDPRComplianceContentProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  return (
    <div className="max-w-4xl mx-auto vscode-text-primary pt-8">
      <h1 className="text-3xl font-bold mb-6 vscode-text-primary">{t('legal.gdprTitle')}</h1>
      
      <div className="prose max-w-none">
        <p className="text-sm vscode-text-secondary mb-6">
          <strong>{t('legal.gdpr.effectiveDate')}:</strong> June 2025<br />
          <strong>{t('legal.gdpr.lastUpdated')}:</strong> October 2025
        </p>

        <p className="text-lg vscode-text-secondary mb-6">{t('legal.gdpr.intro')}</p>

        <div className="mb-8 p-4  ">
          <p className="vscode-text-secondary">
            <strong>{t('legal.gdpr.forEea')}</strong>{' '}
            <a href="/privacy-policy" className="text-blue-400 hover:text-blue-300">{t('legal.gdpr.privacyPolicyLink')}</a>{' '}
            {t('legal.gdpr.providesDetails')}
          </p>
        </div>

        {/* Section 1 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.gdpr.s1Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.gdpr.s1_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s1_1Intro')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.gdpr.s1_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s1_2Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.gdpr.apply1')}</li>
          <li>{t('legal.gdpr.apply2')}</li>
          <li>{t('legal.gdpr.apply3')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.gdpr.s1_3Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s1_3Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.gdpr.commit1')}</li>
          <li>{t('legal.gdpr.commit2')}</li>
          <li>{t('legal.gdpr.commit3')}</li>
          <li>{t('legal.gdpr.commit4')}</li>
          <li>{t('legal.gdpr.commit5')}</li>
          <li>{t('legal.gdpr.commit6')}</li>
          <li>{t('legal.gdpr.commit7')}</li>
        </ul>

        {/* Section 2 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.gdpr.s2Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s2Intro')}</p>

        <div className="space-y-6">
          <div className=" p-6-lg">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">{t('legal.gdpr.right1Title')}</h3>
            <p className="vscode-text-secondary mb-2">{t('legal.gdpr.right1Desc')}</p>
            <p className="text-sm vscode-text-secondary"><strong>{t('legal.gdpr.right1Comply')}</strong></p>
          </div>
          <div className=" p-6-lg">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">{t('legal.gdpr.right2Title')}</h3>
            <p className="vscode-text-secondary mb-2">{t('legal.gdpr.right2Desc')}</p>
            <p className="text-sm vscode-text-secondary mb-2"><strong>{t('legal.gdpr.right2Exercise')}</strong> <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a></p>
            <p className="text-sm vscode-text-secondary"><strong>{t('legal.gdpr.right2Time')}</strong></p>
          </div>
          <div className=" p-6-lg">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">{t('legal.gdpr.right3Title')}</h3>
            <p className="vscode-text-secondary mb-2">{t('legal.gdpr.right3Desc')}</p>
            <p className="text-sm vscode-text-secondary mb-2"><strong>{t('legal.gdpr.right3Exercise')}</strong> <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a></p>
            <p className="text-sm vscode-text-secondary"><strong>{t('legal.gdpr.right3Time')}</strong></p>
          </div>
          <div className=" p-6-lg">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">{t('legal.gdpr.right4Title')}</h3>
            <p className="vscode-text-secondary mb-2">{t('legal.gdpr.right4Desc')}</p>
            <p className="text-sm vscode-text-secondary mb-2"><strong>{t('legal.gdpr.right4Exercise')}</strong> <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a> {t('legal.gdpr.right4Or')}</p>
            <p className="text-sm vscode-text-secondary mb-2"><strong>{t('legal.gdpr.right4Time')}</strong></p>
            <p className="text-sm vscode-text-secondary"><strong>{t('legal.gdpr.right4Note')}</strong></p>
          </div>
          <div className=" p-6-lg">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">{t('legal.gdpr.right5Title')}</h3>
            <p className="vscode-text-secondary mb-2">{t('legal.gdpr.right5Desc')}</p>
            <p className="text-sm vscode-text-secondary mb-2"><strong>{t('legal.gdpr.right5When')}</strong></p>
            <p className="text-sm vscode-text-secondary"><strong>{t('legal.gdpr.right5Exercise')}</strong> <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a></p>
          </div>
          <div className=" p-6-lg">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">{t('legal.gdpr.right6Title')}</h3>
            <p className="vscode-text-secondary mb-2">{t('legal.gdpr.right6Desc')}</p>
            <p className="text-sm vscode-text-secondary mb-2"><strong>{t('legal.gdpr.right6What')}</strong></p>
            <p className="text-sm vscode-text-secondary mb-2"><strong>{t('legal.gdpr.right6Exercise')}</strong> <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a></p>
            <p className="text-sm vscode-text-secondary"><strong>{t('legal.gdpr.right6Time')}</strong></p>
          </div>
          <div className=" p-6-lg">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">{t('legal.gdpr.right7Title')}</h3>
            <p className="vscode-text-secondary mb-2">{t('legal.gdpr.right7Desc')}</p>
            <p className="text-sm vscode-text-secondary mb-2"><strong>{t('legal.gdpr.right7Marketing')}</strong></p>
            <p className="text-sm vscode-text-secondary mb-2"><strong>{t('legal.gdpr.right7Legit')}</strong></p>
            <p className="text-sm vscode-text-secondary"><strong>{t('legal.gdpr.right7Exercise')}</strong> <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a> {t('legal.gdpr.right7Or')}</p>
          </div>
          <div className=" p-6-lg">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">{t('legal.gdpr.right8Title')}</h3>
            <p className="vscode-text-secondary mb-2">{t('legal.gdpr.right8Desc')}</p>
            <p className="text-sm vscode-text-secondary mb-2"><strong>{t('legal.gdpr.right8Ai')}</strong></p>
            <p className="text-sm vscode-text-secondary"><strong>{t('legal.gdpr.right8Control')}</strong></p>
          </div>
        </div>

        {/* Section 3 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.gdpr.s3Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.gdpr.s3_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s3_1Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.gdpr.online1')}</li>
          <li>{t('legal.gdpr.online2')}</li>
          <li>{t('legal.gdpr.online3')}</li>
          <li>{t('legal.gdpr.online4')}</li>
          <li>{t('legal.gdpr.online5')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.gdpr.s3_2Title')}</h3>
        <div className=" p-4-lg mb-4">
          <p className="vscode-text-secondary mb-2"><strong>{t('legal.gdpr.dpoLabel')}</strong></p>
          <p className="vscode-text-secondary">
            {t('legal.gdpr.emailLabel')} <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a>
          </p>
        </div>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s3_2Include')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.gdpr.include1')}</li>
          <li>{t('legal.gdpr.include2')}</li>
          <li>{t('legal.gdpr.include3')}</li>
          <li>{t('legal.gdpr.include4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.gdpr.s3_3Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s3_3Intro')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.gdpr.s3_4Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s3_4Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.gdpr.verify1')}</li>
          <li>{t('legal.gdpr.verify2')}</li>
          <li>{t('legal.gdpr.verify3')}</li>
        </ul>

        {/* Section 4 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.gdpr.s4Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s4Intro')}</p>

        <div className=" p-6-lg mb-4">
          <table className="w-full text-sm vscode-text-secondary">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2">{t('legal.gdpr.purpose')}</th>
                <th className="text-left py-2">{t('legal.gdpr.lawfulBasis')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-700/50">
                <td className="py-2">{t('legal.gdpr.row1Purpose')}</td>
                <td className="py-2">{t('legal.gdpr.row1Basis')}</td>
              </tr>
              <tr className="border-b border-gray-700/50">
                <td className="py-2">{t('legal.gdpr.row2Purpose')}</td>
                <td className="py-2">{t('legal.gdpr.row2Basis')}</td>
              </tr>
              <tr className="border-b border-gray-700/50">
                <td className="py-2">{t('legal.gdpr.row3Purpose')}</td>
                <td className="py-2">{t('legal.gdpr.row3Basis')}</td>
              </tr>
              <tr className="border-b border-gray-700/50">
                <td className="py-2">{t('legal.gdpr.row4Purpose')}</td>
                <td className="py-2">{t('legal.gdpr.row4Basis')}</td>
              </tr>
              <tr className="border-b border-gray-700/50">
                <td className="py-2">{t('legal.gdpr.row5Purpose')}</td>
                <td className="py-2">{t('legal.gdpr.row5Basis')}</td>
              </tr>
              <tr className="border-b border-gray-700/50">
                <td className="py-2">{t('legal.gdpr.row6Purpose')}</td>
                <td className="py-2">{t('legal.gdpr.row6Basis')}</td>
              </tr>
              <tr>
                <td className="py-2">{t('legal.gdpr.row7Purpose')}</td>
                <td className="py-2">{t('legal.gdpr.row7Basis')}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Section 5 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.gdpr.s5Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s5Intro')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.gdpr.s5_1Title')}</h3>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.gdpr.transfer1')}</li>
          <li>{t('legal.gdpr.transfer2')}</li>
          <li>{t('legal.gdpr.transfer3')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.gdpr.s5_2Title')}</h3>
        <div className=" p-4-lg mb-4">
          <ul className="vscode-text-secondary space-y-2">
            <li>🇺🇸 <strong>{t('legal.gdpr.processor1')}</strong></li>
            <li>🇺🇸 <strong>{t('legal.gdpr.processor2')}</strong></li>
            <li>🇮🇳 <strong>{t('legal.gdpr.processor3')}</strong></li>
            <li>🇮🇳 <strong>{t('legal.gdpr.processor4')}</strong></li>
          </ul>
        </div>

        {/* Section 6 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.gdpr.s6Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s6Intro')}</p>
        <div className=" p-6-lg mb-4">
          <p className="vscode-text-secondary mb-2"><strong>{t('legal.gdpr.dpoTitle')}</strong></p>
          <p className="vscode-text-secondary mb-2">
            {t('legal.gdpr.emailLabel')} <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a>
          </p>
          <p className="vscode-text-secondary">{t('legal.gdpr.dpoRole')}</p>
        </div>

        {/* Section 7 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.gdpr.s7Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s7Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.gdpr.breach1')}</li>
          <li>{t('legal.gdpr.breach2')}</li>
          <li>{t('legal.gdpr.breach3')}</li>
        </ul>

        {/* Section 8 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.gdpr.s8Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s8Intro')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.gdpr.s8_1Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s8_1Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>🇪🇺 <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" className="text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer">EU Data Protection Authorities Directory</a></li>
          <li>🇬🇧 <a href="https://ico.org.uk" className="text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer">UK Information Commissioner's Office (ICO)</a></li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.gdpr.s8_2Title')}</h3>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s8_2Intro')}</p>

        {/* Section 9 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.gdpr.s9Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s9Intro')}</p>
        
        {/* Section 10 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.gdpr.s10Title')}</h2>
        <p className="vscode-text-secondary mb-4">{t('legal.gdpr.s10Intro')}</p>
        <ul className="list-disc list-inside vscode-text-secondary mb-4 space-y-2 ml-4">
          <li>{t('legal.gdpr.updates1')}</li>
          <li>{t('legal.gdpr.updates2')}</li>
          <li>{t('legal.gdpr.updates3')}</li>
        </ul>

        {/* Contact Section */}
        <div className="mt-8 p-6  ">
          <h3 className="text-xl font-semibold mb-3">{t('legal.gdpr.contactTitle')}</h3>
          <p className="vscode-text-secondary mb-4">{t('legal.gdpr.contactIntro')}</p>
          <div className="space-y-2 vscode-text-secondary">
            <p><strong>{t('legal.gdpr.emailLabel')}</strong> <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a></p>
            <p><strong>{t('legal.gdpr.supportTeam')}</strong> <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a></p>
            <p><strong>{t('legal.gdpr.websiteLabel')}</strong> <a href="https://www.gograbjob.com" className="text-blue-400 hover:text-blue-300">www.gograbjob.com</a></p>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-8 p-6  ">
          <p className="vscode-text-secondary"><strong>{t('legal.gdpr.quickSummary')}</strong></p>
        </div>

        {/* Related Links */}
        <div className="mt-8 p-4 -lg">
          <p className="text-sm vscode-text-secondary mb-2"><strong>{t('legal.gdpr.relatedTitle')}</strong></p>
          <ul className="text-sm vscode-text-secondary space-y-1">
            <li>• <a href="/privacy-policy" className="text-blue-400 hover:text-blue-300">{t('legal.gdpr.privacyLink')}</a></li>
            <li>• <a href="/cookie-policy" className="text-blue-400 hover:text-blue-300">{t('legal.gdpr.cookieLink')}</a></li>
            <li>• <a href="/terms-of-service" className="text-blue-400 hover:text-blue-300">{t('legal.gdpr.termsLink')}</a></li>
          </ul>
        </div>
      </div>

      {/* Related Policies Navigation */}
      <PolicyNavigation currentPolicy="gdpr" onNavigate={onNavigate} />
    </div>
  );
};

export default GDPRComplianceContent;
